import { useAppKitAccount } from "@reown/appkit/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PropsWithChildren, useCallback, useState } from "react";

import {
  GC_TIME_VERY_LONG,
  QueryKey,
  STALE_TIME_VERY_LONG,
} from "@/constants/shared";
import { HomeBannerContext } from "@/hooks/useHomeBanner";
import {
  makeRequest,
  sendSetUserExperienceMetadataRequest,
} from "@/network/api";
import { TUserExperienceMeta, User } from "@/types";
import {
  getBannerExpirationById,
  getDismissedBannerKeyFromBannerId,
  NHoursInFutureTimestamp,
} from "@/utils";

export const HomeBannerProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(true);
  const { address } = useAppKitAccount();
  const {
    data: user,
    isLoading: isLoadingHomeBannerState,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.UserExperienceMeta, address],
    queryFn: async (): Promise<User> => {
      try {
        const res = await makeRequest("/user");
        const resJson = await res.json();
        return resJson.data;
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    staleTime: STALE_TIME_VERY_LONG,
    gcTime: GC_TIME_VERY_LONG,
  });

  const hasDismissedBannerId = useCallback(
    (bannerId: string) =>
      Boolean(
        user?.userExperienceMeta?.dismissedBanners?.[
          getDismissedBannerKeyFromBannerId(bannerId)
        ],
      ),
    [user],
  );
  const queryClient = useQueryClient();

  const dismissBannerId = useCallback(
    async (bannerId: string) => {
      if (!address) {
        console.log(
          "No wallet address found in HomeBannerProvider for dismiss banner callback",
        );
        return;
      }
      const previousUser = queryClient.getQueryData<User | null>([
        QueryKey.UserExperienceMeta,
        address,
      ]);
      // If there's no previous user, the user is probably just not logged in
      // TODO: maybe add logging for no previousUser case
      if (!previousUser || hasDismissedBannerId(bannerId)) return;
      const newUserMeta: TUserExperienceMeta = {
        ...previousUser.userExperienceMeta,
        dismissedBanners: {
          ...previousUser.userExperienceMeta?.dismissedBanners,
          [getDismissedBannerKeyFromBannerId(bannerId)]: true,
        },
      };
      const optimisticUser: User = {
        ...previousUser,
        userExperienceMeta: newUserMeta,
      };
      queryClient.setQueryData<User | null>(
        [QueryKey.UserExperienceMeta, address],
        optimisticUser,
      );
      try {
        await sendSetUserExperienceMetadataRequest(newUserMeta);
        await refetch();
      } catch (e) {
        queryClient.setQueryData(
          [QueryKey.UserExperienceMeta, address],
          previousUser,
        );
        console.error(`Error during dismissBannerId`, e);
      }
    },
    [address, hasDismissedBannerId, queryClient, refetch],
  );

  const bannerIsExpired = useCallback(
    (bannerId: string) => {
      const expTimestampString =
        user?.userExperienceMeta?.bannerExpirations?.[
          getBannerExpirationById(bannerId)
        ];
      if (!expTimestampString) return false;
      const expiration = Number(expTimestampString);
      return Date.now() > expiration;
    },
    [user?.userExperienceMeta],
  );

  const checkAndSet48HrBannerExpiration = useCallback(
    async (bannerId: string) => {
      const previousUser = queryClient.getQueryData<User | null>([
        QueryKey.UserExperienceMeta,
        address,
      ]);
      // If there's no previous user, the user is probably just not logged in
      // TODO: maybe add logging for no previousUser case
      if (!previousUser) return;
      if (bannerIsExpired(bannerId)) {
        await dismissBannerId(bannerId);
        return;
      }
      const newUserMeta: TUserExperienceMeta = {
        ...previousUser.userExperienceMeta,
        bannerExpirations: {
          ...previousUser.userExperienceMeta?.bannerExpirations,
          [getBannerExpirationById(bannerId)]: NHoursInFutureTimestamp(48),
        },
      };
      try {
        await sendSetUserExperienceMetadataRequest(newUserMeta);
        await refetch();
      } catch (e) {
        queryClient.setQueryData(
          [QueryKey.UserExperienceMeta, address],
          previousUser,
        );
        console.error(`Error during checkAndSet48HrBannerExpiration`, e);
      }
    },
    [queryClient, address, bannerIsExpired, dismissBannerId, refetch],
  );

  return (
    <HomeBannerContext.Provider
      value={{
        open,
        setOpen,
        hasDismissedBannerId,
        dismissBannerId,
        checkAndSet48HrBannerExpiration,
        isLoadingHomeBannerState,
      }}
    >
      {children}
    </HomeBannerContext.Provider>
  );
};
